'use client';

/**
 * THE DESK READ + THE TAPE — the v3 lead of THE INTERCEPTS (sketch:
 * docs/design/intercepts-prototype/intercepts-v3.html).
 *
 * THE LAW: the page leads with the agent's newest conclusion. That conclusion
 * already exists — the daily recap (`ecosystem_recaps`, 12:00 UTC) — so the band
 * renders it VERBATIM (title + summary), with zero new pipeline:
 *
 *   · DeskRead      — recap headline + summary, sentiment pill, "named in the read"
 *                     focus chips (recap.highlights @tokens resolved against the
 *                     tracked entity roster), provenance microline (sourceCounts),
 *                     link to the full brief (/daily/[date]/).
 *   · TapeScrubber  — the temporal record: last 14 calendar days; bar height =
 *                     that day's active-account count (recap.sourceCounts
 *                     .activityPulses); click a past day to replay it (?day=).
 *   · ArchiveBanner — shown while scrubbed: names the day's read of record.
 *
 * Data arrives as plain-JSON `DeskTapeDay[]` props from the SERVER route (the
 * recap fetch is Admin-SDK, SSR'd — the conclusion is in the crawlable HTML).
 * Token-native, gold-only. parchment-faint only ever as a background.
 */

import { recapHref } from '@/lib/recap/recap-links';
import { useMemo } from 'react';
import type { EntitySearchData } from '@/types/tags';
import { handleTokensFromHighlights, type DeskTapeDay } from './desk-tape';

const MONO = 'var(--font-mono)';
const SANS = 'var(--font-sans)';

/* Sentiment pill — recap.sentiment. Gold-free greens/reds match the dashboard idiom. */
const SENTIMENT_PILL: Record<string, React.CSSProperties> = {
  bullish: { color: 'var(--green-text)', background: 'var(--green-dim)', border: '0.5px solid rgba(85,201,133,0.35)' },
  bearish: { color: 'var(--red-text)', background: 'var(--red-dim)', border: '0.5px solid rgba(224,92,74,0.45)' },
};
const SENTIMENT_PILL_DEFAULT: React.CSSProperties = {
  color: 'var(--parchment-dim)',
  background: 'var(--parchment-faint)',
  border: '0.5px solid var(--divider)',
};

function fmtDayLong(date: string): string {
  // YYYY-MM-DD → "Wed Jun 11" (UTC; recap days are UTC days).
  const d = new Date(`${date}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function fmtUtcTime(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
}

/* ── THE DESK READ band ─────────────────────────────────────────────────────────── */
export function DeskRead({
  read,
  isToday,
  entities,
  onEntitySelect,
}: {
  /** The newest published recap day (deskTape[0]). */
  read: DeskTapeDay;
  /** Whether the read covers TODAY (UTC) — else the meta line says when the next lands. */
  isToday: boolean;
  /** The tracked entity roster (UNfiltered) — resolves highlight @tokens into chips. */
  entities: EntitySearchData[];
  onEntitySelect: (entityId: string, entitySlug: string) => void;
}) {
  // THE SOCIAL DESK READ overlay: when a desk_reads doc exists for this day the
  // band renders the SYNTHESIZED read; otherwise it falls back BYTE-FOR-BYTE to
  // the recap headline/summary below. `synth` is the overlay (or undefined).
  const synth = read.read;
  const synthHandleSet = useMemo(() => {
    const s = new Set<string>();
    for (const nb of synth?.notable ?? []) {
      if (nb.handle) s.add(nb.handle.replace(/^@/, '').toLowerCase());
    }
    return s;
  }, [synth]);

  // "Named in the read" — the focusable entity chips. With a desk read present,
  // resolve from its `notable` handles; otherwise from the recap highlights'
  // @tokens (the original behavior). Only resolvable entities render; cap 6.
  const named = useMemo(() => {
    const tokens = synth
      ? Array.from(synthHandleSet)
      : handleTokensFromHighlights(read.highlights);
    if (tokens.length === 0 || entities.length === 0) return [];
    const byHandle = new Map<string, EntitySearchData>();
    for (const e of entities) {
      for (const h of e.twitterHandles ?? []) byHandle.set(h.toLowerCase(), e);
    }
    const out: EntitySearchData[] = [];
    const seen = new Set<string>();
    for (const t of tokens) {
      const e = byHandle.get(t);
      if (e && !seen.has(e.id)) {
        seen.add(e.id);
        out.push(e);
        if (out.length >= 6) break;
      }
    }
    return out;
  }, [synth, synthHandleSet, read.highlights, entities]);

  // Sentiment + dateline timestamp prefer the desk read's own values when present.
  const sentiment = synth?.sentiment ?? read.sentiment;
  const pill = SENTIMENT_PILL[(sentiment ?? '').toLowerCase()] ?? SENTIMENT_PILL_DEFAULT;
  const compiledAt = fmtUtcTime(synth?.generatedAt ?? read.publishedAt);
  // Provenance counts: from the desk read's inputStats when synthesized, else the
  // recap's source counts (byte-identical fallback).
  const provTweets = synth?.inputStats?.tweetCount;
  const provPulses = synth ? synth.inputStats?.pulseCount ?? read.pulses : read.pulses;

  return (
    <section
      aria-label="The desk read — the agent's latest conclusion"
      style={{
        border: '0.5px solid var(--amber-line)',
        borderRadius: 6,
        background: 'var(--surface)',
        padding: '16px 20px 15px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* gold corner glow (matches ON THE AIR's idiom) */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'radial-gradient(ellipse 70% 140% at 0% 0%, rgba(232,168,50,0.06) 0%, transparent 55%)',
        }}
      />

      {/* header row: eyebrow · sentiment · date/meta */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9, flexWrap: 'wrap' }}>
        <span
          style={{
            fontFamily: MONO,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--amber)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)', boxShadow: '0 0 7px var(--amber)' }} />
          The desk read
        </span>
        {sentiment && (
          <span
            style={{
              fontFamily: MONO,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 3,
              ...pill,
            }}
          >
            {sentiment}
          </span>
        )}
        <span
          style={{
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--parchment-dim)',
            marginLeft: 'auto',
          }}
        >
          {synth
            ? `Social desk · synthesized${compiledAt ? ` ${compiledAt}` : ''}`
            : isToday
              ? `${fmtDayLong(read.date)}${compiledAt ? ` · compiled ${compiledAt}` : ''}`
              : `${fmtDayLong(read.date)} · next read ~12:00 UTC`}
        </span>
      </div>

      {/* the conclusion. With a desk read: the SYNTHESIZED one-paragraph read,
          verbatim. Without: recap title + summary, VERBATIM (byte-for-byte fallback). */}
      {synth ? (
        <p
          style={{
            position: 'relative',
            margin: 0,
            fontFamily: SANS,
            fontSize: 15.5,
            lineHeight: 1.6,
            color: 'var(--parchment)',
            maxWidth: '80ch',
          }}
        >
          {synth.text}
        </p>
      ) : (
        <>
          <h2
            style={{
              position: 'relative',
              margin: 0,
              fontFamily: SANS,
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              color: 'var(--parchment)',
              maxWidth: '62ch',
            }}
          >
            {read.title}
          </h2>
          {read.summary && (
            <p
              style={{
                position: 'relative',
                margin: '6px 0 0',
                fontSize: 14,
                lineHeight: 1.55,
                color: '#D8D8D4',
                maxWidth: '80ch',
              }}
            >
              {read.summary}
            </p>
          )}
        </>
      )}

      {/* named in the read — focusable entity chips from the highlights' @tokens */}
      {named.length > 0 && (
        <div style={{ position: 'relative', marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
          <span
            style={{
              fontFamily: MONO,
              fontSize: 9,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--slate)',
              marginRight: 2,
            }}
          >
            Named in the read
          </span>
          {named.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => onEntitySelect(e.id, e.slug)}
              title={`Focus ${e.name}`}
              style={{
                fontFamily: SANS,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--amber)',
                border: '0.5px solid var(--amber-line)',
                background: 'var(--amber-dim)',
                borderRadius: 999,
                padding: '3px 11px',
                cursor: 'pointer',
              }}
            >
              {e.name}
            </button>
          ))}
        </div>
      )}

      {/* provenance microline — sourceCounts + the brief link */}
      <div
        style={{
          position: 'relative',
          marginTop: 11,
          paddingTop: 9,
          borderTop: '0.5px solid var(--divider)',
          fontFamily: MONO,
          fontSize: 9.5,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--slate)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 14,
          alignItems: 'center',
        }}
      >
        {synth ? (
          <>
            {typeof provTweets === 'number' && (
              <span>
                read from <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{provTweets} tweets</b>
              </span>
            )}
            <span>
              <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{provPulses} pulses</b>
            </span>
          </>
        ) : (
          <>
            <span>
              compiled from <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{read.pulses} pulses</b>
            </span>
            <span>
              <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{read.breaking} breaking</b>
            </span>
            <span>
              <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{read.featuredCount} featured intercepts</b>
            </span>
          </>
        )}
        <a
          href={recapHref(read.date, read.chain)}
          style={{
            marginLeft: 'auto',
            color: 'var(--amber)',
            borderBottom: '1px solid var(--amber-line)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Read the full brief →
        </a>
      </div>
    </section>
  );
}

/* ── THE TAPE — 14-day scrubber ─────────────────────────────────────────────────── */
const TAPE_WINDOW = 14;
const BAR_MAX = 26; // px
const BAR_MIN = 3; // px — a day with no read still gets a scrub-able stub

export function TapeScrubber({
  tape,
  selectedDay,
  onSelectDay,
}: {
  tape: DeskTapeDay[];
  /** YYYY-MM-DD currently scrubbed to (undefined = live/today). */
  selectedDay?: string;
  /** null = jump back to today (live). */
  onSelectDay: (date: string | null) => void;
}) {
  // Calendar-driven: last 14 UTC days ending today, annotated with recap data where
  // a read exists. Days without a read are still scrubbable (the tweets exist).
  const days = useMemo(() => {
    const byDate = new Map(tape.map((d) => [d.date, d]));
    const out: Array<{ date: string; label: string; read?: DeskTapeDay; isToday: boolean }> = [];
    const now = Date.now();
    for (let i = TAPE_WINDOW - 1; i >= 0; i--) {
      const d = new Date(now - i * 86_400_000);
      const date = d.toISOString().slice(0, 10);
      out.push({
        date,
        label: String(d.getUTCDate()),
        read: byDate.get(date),
        isToday: i === 0,
      });
    }
    return out;
  }, [tape]);

  const maxPulses = Math.max(1, ...days.map((d) => d.read?.pulses ?? 0));

  return (
    <div
      role="group"
      aria-label="The tape — scrub to a past day"
      className="tape-scrub"
      style={{
        marginTop: 10,
        border: '0.5px solid var(--divider)',
        borderRadius: 6,
        background: 'var(--surface)',
        padding: '10px 16px 8px',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 12,
        overflowX: 'auto',
      }}
    >
      <span
        style={{
          fontFamily: MONO,
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--slate)',
          paddingBottom: 15,
          flexShrink: 0,
        }}
      >
        The tape
      </span>
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 3, minWidth: 320 }}>
        {days.map((d) => {
          const on = d.isToday ? !selectedDay : selectedDay === d.date;
          const h = d.read ? Math.max(BAR_MIN + 2, Math.round((d.read.pulses / maxPulses) * BAR_MAX)) : BAR_MIN;
          const title = d.isToday
            ? 'Today — live'
            : d.read
              ? `${fmtDayLong(d.date)} · ${d.read.pulses} accounts active · ${d.read.title}`
              : `${fmtDayLong(d.date)} · no read on record`;
          return (
            <button
              key={d.date}
              type="button"
              onClick={() => onSelectDay(d.isToday ? null : on ? null : d.date)}
              aria-pressed={on}
              title={title}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 5,
                padding: '3px 2px 2px',
                borderRadius: 4,
                background: on ? 'var(--amber-dim)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: '100%',
                  maxWidth: 22,
                  height: d.isToday ? BAR_MAX * 0.75 : h,
                  borderRadius: '2px 2px 0 0',
                  background: on ? 'var(--amber)' : 'var(--divider)',
                }}
              />
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: 8.5,
                  letterSpacing: '0.04em',
                  color: on ? 'var(--amber)' : 'var(--slate)',
                  fontWeight: on ? 700 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {d.isToday ? 'LIVE' : d.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── ARCHIVE banner — shown while scrubbed to a past day ────────────────────────── */
export function ArchiveBanner({
  date,
  read,
  onJumpToToday,
}: {
  date: string;
  read?: DeskTapeDay;
  onJumpToToday: () => void;
}) {
  return (
    <div
      style={{
        border: '0.5px solid var(--amber-line)',
        borderLeft: '3px solid var(--amber)',
        borderRadius: 6,
        background: 'var(--amber-dim)',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontFamily: MONO,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--amber)',
          flexShrink: 0,
        }}
      >
        ◷ Archive · {fmtDayLong(date)}
      </span>
      <span style={{ fontSize: 12.5, color: 'var(--parchment-dim)', minWidth: 0 }}>
        {read ? (
          <>Read of record: “{read.title}”</>
        ) : (
          <>No desk read on record for this day — raw intercepts below.</>
        )}
      </span>
      <button
        type="button"
        onClick={onJumpToToday}
        style={{
          marginLeft: 'auto',
          fontFamily: MONO,
          fontSize: 9.5,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--parchment)',
          border: '0.5px solid var(--amber-line)',
          borderRadius: 4,
          padding: '4px 10px',
          background: 'transparent',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Jump to today →
      </button>
    </div>
  );
}
